import { useState } from "react";
import { useAuth } from "@/auth/AuthProvider";
import { Boton, Campo, Dialogo, Input, Tarjeta } from "@/components/ui";
import { useToast } from "@/components/ui/Toast";
import { api, apiRaw, ApiError } from "@/lib/api";
import { supabase } from "@/lib/supabase";
import { asesorHasta } from "@/auth/Consentimiento";
import { CORREO_PRIVACIDAD, PRIVACY_VERSION } from "@/legal/aviso";
import { useDatos } from "@/features/datos/store/store";
import { borrarLocal } from "@/features/datos/persistencia/local";

/** Los interruptores que exponen datos piden confirmación (revisión de Diego #11): un clic engañado desde otro sitio no basta. */
type Confirmacion = { tipo: "asesor" } | { tipo: "sensibles"; valor: boolean } | null;

export function Cuenta() {
  const { member, salir, recargar, estado, sensibles, privacyVersion } = useAuth();
  const toast = useToast();
  const [confirm, setConfirm] = useState("");
  const [borrando, setBorrando] = useState(false);
  const [modo, setModo] = useState(member?.mode ?? "");
  const [confirmacion, setConfirmacion] = useState<Confirmacion>(null);

  async function exportar() {
    try {
      const res = await apiRaw("/api/me/export");
      if (!res.ok) throw new Error("export");
      const blob = await res.blob();
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob); a.download = "mis-datos-wealth-nation.json"; a.click();
      URL.revokeObjectURL(a.href);
    } catch { toast.error("No pude preparar tu descarga. Intenta de nuevo."); }
  }

  async function borrar() {
    if (!member || confirm.trim().toLowerCase() !== member.email.toLowerCase()) return;
    setBorrando(true);
    try {
      await api("/api/me/delete", { method: "POST", json: { confirm: confirm.trim().toLowerCase() } });
      await salir();
    } catch (e) { toast.error(e instanceof ApiError ? e.message : "No pude borrar tu cuenta."); setBorrando(false); }
  }

  const asesorActivo = !!member?.share_with_facilitator_until && new Date(member.share_with_facilitator_until) > new Date();
  const [asesor, setAsesor] = useState(asesorActivo);
  async function guardarAsesor(v: boolean) {
    setAsesor(v);
    const { error } = await supabase.from("members").update({ share_with_facilitator_until: v ? asesorHasta() : null }).eq("id", member!.id);
    if (error) { toast.error("No pude guardar tu preferencia."); setAsesor(!v); } else { toast.ok(v ? "Listo: tu asesor podrá ver tu foto." : "Listo: solo tú ves tus datos."); await recargar(); }
  }
  /** Activar pide confirmación (expone la foto a un tercero); apagar no. */
  function pedirAsesor(v: boolean) { if (v) setConfirmacion({ tipo: "asesor" }); else void guardarAsesor(false); }

  const [guardandoSensibles, setGuardandoSensibles] = useState(false);
  async function guardarSensibles(v: boolean) {
    setGuardandoSensibles(true);
    try {
      await api("/api/me/sensibles", { method: "POST", json: { acepto: v, version: privacyVersion ?? PRIVACY_VERSION } });
      if (!v && member) {
        // El servidor ya vació esas secciones en sus fotos y su borrador: el formulario se vuelve a sembrar desde ahí, sin la copia local
        borrarLocal(member.id);
        useDatos.getState().reset();
      }
      await recargar();
      toast.ok(v ? "Listo: puedes contestar Mi identidad, el Diagnóstico y la Rueda 360." : "Listo: revocaste la autorización y borramos esas respuestas de tus fotos.");
    } catch (e) { toast.error(e instanceof ApiError ? e.message : "No pude guardar tu preferencia."); }
    setGuardandoSensibles(false);
  }

  async function guardarModo(m: string) {
    setModo(m);
    const { error } = await supabase.from("members").update({ mode: m || null }).eq("id", member!.id);
    if (error) toast.error("No pude guardar tu preferencia."); else { toast.ok("Listo."); await recargar(); }
  }

  function confirmar() {
    const c = confirmacion;
    setConfirmacion(null);
    if (!c) return;
    if (c.tipo === "asesor") void guardarAsesor(true);
    else void guardarSensibles(c.valor);
  }

  return (
    <div className="flex flex-col gap-4">
      <h1 className="text-xl font-semibold">Cuenta</h1>
      <Tarjeta titulo="Tu acceso">
        <p className="text-sm"><b>{member?.email}</b> · {member?.cohort ?? "miembro"} · aviso de privacidad {member?.consent_version ?? "—"}</p>
        <Boton variante="secundario" className="mt-3" onClick={salir}>Salir</Boton>
      </Tarjeta>
      {estado && (
        <Tarjeta titulo="Uso de tu asesor este mes">
          <div className="h-2 w-full overflow-hidden rounded-full bg-fondo"><div className="h-full rounded-full" style={{ width: `${Math.round(estado.usage.pct * 100)}%`, background: estado.usage.warn ? "var(--color-alerta, #a8801f)" : "var(--viz-serie)" }} /></div>
          <p className="mt-2 text-sm text-tinta-suave">{Math.round(estado.usage.pct * 100)}% usado. {estado.usage.cap_reached ? "El asesor descansa hasta el día 1; tu foto y tus reportes siguen disponibles." : "Se renueva el día 1 de cada mes."}</p>
        </Tarjeta>
      )}
      <Tarjeta titulo="Cómo quieres que te hable el asesor">
        <div className="flex flex-col gap-2 text-sm">
          <label className="flex items-start gap-3 rounded-md border border-borde p-3"><input type="radio" name="modo" className="mt-1" checked={modo === "metodo"} onChange={() => guardarModo("metodo")} /><span><b>Dame el método.</b> Denso y directo: el dato, el criterio y el siguiente paso. Sin explicar lo básico.</span></label>
          <label className="flex items-start gap-3 rounded-md border border-borde p-3"><input type="radio" name="modo" className="mt-1" checked={modo === "acompaname"} onChange={() => guardarModo("acompaname")} /><span><b>Acompáñame.</b> Una idea a la vez, cada término explicado la primera vez, misma honestidad.</span></label>
        </div>
      </Tarjeta>
      <Tarjeta titulo="Quién ve tus datos">
        <ul className="flex flex-col gap-2 text-sm text-tinta-suave">
          <li><b className="text-tinta">Tú:</b> todo.</li>
          <li><b className="text-tinta">Tu asesor de Revolution Wealth Management:</b> solo si lo activas aquí. Ve tu foto patrimonial (tus números y tu diagnóstico), nunca tus conversaciones con el asesor IA.</li>
          <li><b className="text-tinta">El equipo técnico:</b> únicamente para operar la plataforma, con bitácora. Las respuestas del asesor IA marcadas como dudosas se revisan sin tu nombre.</li>
        </ul>
        <label className="mt-3 flex items-start gap-3 rounded-md border border-borde p-3 text-sm">
          <input type="checkbox" className="mt-1 h-5 w-5" checked={asesor} onChange={(e) => pedirAsesor(e.target.checked)} />
          <span><b>Mi asesor puede ver mi foto patrimonial.</b> {asesor && member?.share_with_facilitator_until ? `Activo hasta el ${new Date(member.share_with_facilitator_until).toLocaleDateString("es-MX", { day: "numeric", month: "short", year: "numeric" })}; lo puedes apagar cuando quieras.` : "Apagado: nadie más que tú ve tus datos."}</span>
        </label>
      </Tarjeta>
      <Tarjeta titulo="Datos sensibles">
        <p className="text-sm text-tinta-suave">Mi identidad, el Diagnóstico y la Rueda 360 preguntan salud, bienestar emocional, creencias y vida familiar. Solo se contestan con tu autorización expresa.</p>
        <label className="mt-3 flex items-start gap-3 rounded-md border border-borde p-3 text-sm">
          <input type="checkbox" className="mt-1 h-5 w-5" checked={sensibles} disabled={guardandoSensibles} onChange={(e) => setConfirmacion({ tipo: "sensibles", valor: e.target.checked })} />
          <span><b>Autorizo el tratamiento de mis datos sensibles.</b> {sensibles ? "Activo; lo puedes revocar cuando quieras." : "Sin autorizar: esas tres secciones quedan cerradas."}</span>
        </label>
      </Tarjeta>
      <Tarjeta titulo="Tus datos">
        <p className="text-sm text-tinta-suave">Descarga todo lo que guardamos de ti (celdas, fotos, conversaciones, reportes) en un archivo JSON.</p>
        <Boton variante="secundario" className="mt-3" onClick={exportar}>Descargar mis datos</Boton>
      </Tarjeta>
      <Tarjeta titulo="Borrar mi cuenta">
        <p className="text-sm text-tinta-suave">Borra tus celdas, fotos, conversaciones y reportes de inmediato. No se puede deshacer. Para confirmar, escribe tu correo.</p>
        <div className="mt-3 flex flex-col gap-3">
          <Campo etiqueta="Tu correo" htmlFor="confirm"><Input id="confirm" value={confirm} onChange={(e) => setConfirm(e.target.value)} placeholder={member?.email} /></Campo>
          <Boton variante="peligro" disabled={!member || confirm.trim().toLowerCase() !== member.email.toLowerCase()} cargando={borrando} onClick={borrar}>Borrar todo</Boton>
        </div>
      </Tarjeta>
      <p className="text-xs text-tinta-suave"><a className="underline" href="/aviso-de-privacidad" target="_blank" rel="noreferrer">Aviso de privacidad</a> · Derechos ARCO: {CORREO_PRIVACIDAD}.</p>

      <Dialogo abierto={confirmacion?.tipo === "asesor"} onCerrar={() => setConfirmacion(null)} titulo="¿Compartir tu foto con tu asesor?">
        <p className="text-sm text-tinta-suave">Tu asesor de Revolution Wealth Management verá tu foto patrimonial completa (números y diagnóstico) durante un año. No verá tus conversaciones con el asesor IA. Lo puedes apagar cuando quieras.</p>
        <div className="mt-4 flex justify-end gap-2"><Boton variante="secundario" onClick={() => setConfirmacion(null)}>Cancelar</Boton><Boton onClick={confirmar}>Sí, compartir</Boton></div>
      </Dialogo>
      <Dialogo abierto={confirmacion?.tipo === "sensibles"} onCerrar={() => setConfirmacion(null)} titulo={confirmacion?.tipo === "sensibles" && confirmacion.valor ? "¿Autorizar tus datos sensibles?" : "¿Revocar la autorización?"}>
        {confirmacion?.tipo === "sensibles" && confirmacion.valor ? (
          <p className="text-sm text-tinta-suave">Podrás contestar Mi identidad, el Diagnóstico y la Rueda 360, y el asesor IA los usará para orientarte. Lo puedes revocar cuando quieras.</p>
        ) : (
          <p className="text-sm text-tinta-suave">Se borran tus respuestas de Mi identidad, el Diagnóstico y la Rueda 360 de todas tus fotos y de tu borrador, y el asesor IA deja de usarlas. Tus números no se tocan. Si vuelves a autorizar, tendrás que contestarlas de nuevo.</p>
        )}
        <div className="mt-4 flex justify-end gap-2"><Boton variante="secundario" onClick={() => setConfirmacion(null)}>Cancelar</Boton><Boton variante={confirmacion?.tipo === "sensibles" && !confirmacion.valor ? "peligro" : "primario"} onClick={confirmar}>{confirmacion?.tipo === "sensibles" && confirmacion.valor ? "Sí, autorizar" : "Sí, revocar y borrar"}</Boton></div>
      </Dialogo>
    </div>
  );
}
